import React, { Component } from 'react';

/**
 * Error Boundary component to catch JavaScript errors in child component tree
 * and display a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console (could also send to error reporting service)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI based on component name
      const { fallback, componentName } = this.props;

      if (fallback) {
        return fallback;
      }

      return (
        <div className="error-boundary-fallback">
          <div className="error-content">
            <div className="error-icon">⚠️</div>
            <h2>Something went wrong</h2>
            <p className="error-component">
              {componentName ? `Error in ${componentName}` : 'A component failed to load'}
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error Details</summary>
                <pre>{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </details>
            )}
            <div className="error-actions">
              <button onClick={this.handleRetry} className="retry-btn">
                Try Again
              </button>
              <button onClick={this.handleReload} className="reload-btn">
                Reload Page
              </button>
            </div>
          </div>
          <style>{`
            .error-boundary-fallback {
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 200px;
              padding: 20px;
              background: #1a1a2e;
              border-radius: 8px;
              margin: 10px;
            }
            .error-content {
              text-align: center;
              color: #e0e0e0;
              max-width: 500px;
            }
            .error-icon {
              font-size: 48px;
              margin-bottom: 16px;
            }
            .error-content h2 {
              color: #ff6b6b;
              margin: 0 0 8px 0;
              font-size: 1.5rem;
            }
            .error-component {
              color: #888;
              margin: 0 0 16px 0;
              font-size: 0.9rem;
            }
            .error-details {
              text-align: left;
              margin: 16px 0;
              padding: 12px;
              background: #0d0d1a;
              border-radius: 4px;
              font-size: 0.75rem;
            }
            .error-details summary {
              cursor: pointer;
              color: #888;
              margin-bottom: 8px;
            }
            .error-details pre {
              margin: 8px 0;
              padding: 8px;
              background: #000;
              border-radius: 4px;
              overflow-x: auto;
              white-space: pre-wrap;
              word-break: break-word;
              color: #ff6b6b;
            }
            .error-actions {
              display: flex;
              gap: 12px;
              justify-content: center;
            }
            .error-actions button {
              padding: 10px 20px;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-weight: 600;
              transition: opacity 0.2s;
            }
            .error-actions button:hover {
              opacity: 0.9;
            }
            .retry-btn {
              background: #4ade80;
              color: #000;
            }
            .reload-btn {
              background: #333;
              color: #fff;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
